import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { isHoverForInteractiveType, saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { ShapeType } from '@/types/commontypes';
import type { ElliottImpulseProps } from './ElliottImpulse';
import type { ElliottImpulseSharedState, MoreProps } from '../../../types/commontypes';
import { InteractiveStraightLine } from './Wrapper/InteractiveStraightLine';
import { EachElliottImpulse } from './Wrapper/EachElliottImpulse';
import {
  applyYClampIfNeeded,
  completeOrExtendPath,
  handleDoubleClick,
  handleDrawLine,
  handleHover,
  handleSelect,
} from '@/utils/impulseUtils';
import { ClickableCircle } from './Wrapper';
import { ChartShapesContext } from '../../../contexts';
import { toPathRFCProps, RFC_RENDER_MODE } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import type { MultiPointDragOverride, PathRenderNode } from '../../../types/commontypes';

interface PathState {
  override?: MultiPointDragOverride;
  elliottImpulses?: unknown;
}

const PATH_POINT_LIMIT = 100;

export class PathImpulse extends React.Component<ElliottImpulseProps, PathState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: Record<string, any> | null = null;

  static get defaultProps() {
    if (!PathImpulse._cachedDefaultProps) {
      PathImpulse._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#1e53e6',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 0,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        elliottImpulses: [],
      };
    }
    return PathImpulse._cachedDefaultProps;
  }

  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved: boolean = false;
  private saveNodeType: <T>(idx: number | string) => React.Ref<T>;
  private clickTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastClickTime: number = 0;
  private suppressNextDoubleClick: boolean = false;

  public constructor(props: ElliottImpulseProps) {
    super(props);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('elliottImpulses').bind(this);

    this.state = {};
  }

  public render() {
    const {
      type,
      enabled,
      hoverText,
      elliottImpulses,
      currentPositionstrokeOpacity,
      currentPositionRadius = PathImpulse.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      shouldDisableSnap,
      snap,
      snapTo,
      sharedState,
      chartId,
      isAnyShapeEnabled,
    } = this.props;

    const rfcMode = RFC_RENDER_MODE[type];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(type ?? ShapeType.PATH) as PathRenderNode;
    const appearance = toPathRFCProps(activeNode) as unknown as {
      strokeStyle: string;
      strokeWidth: number;
      strokeDasharray: string;
      r: number;
      edgeFill: string;
      edgeStroke: string;
      edgeStrokeWidth: number;
    };
    const { override } = this.state;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const { currentPoints, status, endXY } = sharedState ?? ({} as ElliottImpulseSharedState);
    const tempCondition =
      isDefined(currentPoints) &&
      isDefined(endXY) &&
      currentPoints.length > 0 &&
      currentPoints.length < PATH_POINT_LIMIT &&
      sharedState?.type === type &&
      chartId === sharedState?.chartId &&
      status !== 'completed' &&
      enabled;

    const tempLine = tempCondition ? (
      <>
        {/* Draw temp straight line to mouse */}
        <InteractiveStraightLine
          ref={this.saveNodeType('dotted-line')}
          x1Value={currentPoints[currentPoints.length - 1][0]}
          y1Value={currentPoints[currentPoints.length - 1][1]}
          x2Value={endXY[0]}
          y2Value={endXY[1]}
          strokeStyle={appearance.strokeStyle}
          strokeWidth={appearance.strokeWidth}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          strokeDasharray={appearance.strokeDasharray as never}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          type={rfcMode as 'XLINE' | 'RAY' | 'LINE'}
        />
      </>
    ) : null;

    return (
      <g>
        {/* Circles */}
        {elliottImpulses.map((impulse, index) => {
          if (impulse.hidden) return null;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = toPathRFCProps(impulse as unknown as PathRenderNode);
          const hoverTextWithDefault = {
            ...PathImpulse.defaultProps.hoverText,
            ...hoverText,
          };

          let points = impulse.points;
          if (override && override.uuid === impulse.uuid) {
            if (isDefined(override.newPoints)) {
              points = override.newPoints;
            } else if (isDefined(override.pointIdx) && isDefined(override.newPoint)) {
              const pIdx = override.pointIdx;
              const pNew = override.newPoint;
              points = impulse.points.map((pt: [number, number], i: number) => (i === pIdx ? pNew : pt));
            }
          }
          const selected = impulse.selected && sharedState?.status !== 'started';
          return (
            <EachElliottImpulse
              key={impulse.uuid}
              ref={this.saveNodeType(index)}
              index={index}
              uuid={impulse.uuid}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={RFC_RENDER_MODE[impulse.type] as 'XLINE' | 'RAY' | 'LINE'}
              selected={selected}
              points={points}
              appearance={eachAppearance}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              edgeInteractiveCursor="react-financial-charts-move-cursor"
              lineInteractiveCursor="react-financial-charts-pointer-cursor"
              hoverText={hoverTextWithDefault}
              onSelect={(e: React.MouseEvent<Element, MouseEvent>, _uuid: string, moreProps: MoreProps) =>
                handleSelect(e, impulse.uuid, moreProps, this.props)
              }
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
              onDragStart={(uuid: string) => this.setState({ override: { uuid } })}
              onHover={(id: string | number | null, moreProps?: MoreProps) => handleHover(id, moreProps, this.props)}
              locked={impulse.locked}
              onDoubleClick={(e: React.MouseEvent, mp: MoreProps) => {
                if (this.suppressNextDoubleClick) {
                  this.suppressNextDoubleClick = false; // Reset it
                  e.stopPropagation(); // optional: also stops bubbling
                  return;
                }

                handleDoubleClick(e, mp, this.props);
              }}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              sharedState={(sharedState ?? {}) as unknown as ElliottImpulseSharedState}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {currentPoints && currentPoints.length > 0 && currentPoints.length < PATH_POINT_LIMIT && tempCondition && (
          <g>
            {currentPoints.length > 1 &&
              currentPoints.slice(0, -1).map((start, idx) => {
                const end = currentPoints[idx + 1];
                return (
                  <InteractiveStraightLine
                    key={`current-line-${idx}`}
                    x1Value={start[0]}
                    y1Value={start[1]}
                    x2Value={end[0]}
                    y2Value={end[1]}
                    strokeStyle={appearance.strokeStyle}
                    strokeWidth={appearance.strokeWidth}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    strokeDasharray={appearance.strokeDasharray as never}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    type={rfcMode as 'XLINE' | 'RAY' | 'LINE'}
                  />
                );
              })}
            {currentPoints?.map(([x, y]: [number, number], idx: number) => {
              return (
                <g key={`currentGroup-${idx}`}>
                  <ClickableCircle
                    key={`current-circle-${idx}`}
                    cx={x}
                    cy={y}
                    r={appearance.r}
                    fillStyle={appearance.edgeFill}
                    strokeStyle={appearance.edgeStroke}
                    strokeWidth={appearance.edgeStrokeWidth}
                    show={true}
                  />
                </g>
              );
            })}
          </g>
        )}
        {tempLine}

        {/* Click listener */}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onMouseMove={(e: React.MouseEvent, xy: number[], moreProps: MoreProps) => {
            this.mouseMoved = true;

            handleDrawLine(e, xy, moreProps, this.props);
          }}
        />
      </g>
    );
  }

  public componentDidUpdate(prevProps: ElliottImpulseProps) {
    const { completePath, sharedState, type } = this.props;
    if (type !== ShapeType.PATH && completePath === false) return;
    // Only react when completePath flips to true
    if (
      completePath === true &&
      prevProps.completePath !== true &&
      sharedState?.type === ShapeType.PATH &&
      sharedState?.status === 'started' &&
      sharedState?.currentPoints?.length > 1
    ) {
      // Fake event + moreProps (they are not used)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
      const fakeEvent = {} as React.MouseEvent;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
      const fakeMoreProps = {} as MoreProps;
      // Use last point as final point
      const lastPoint = sharedState.currentPoints[sharedState.currentPoints.length - 1];

      this.handleEnd(
        fakeEvent,
        lastPoint,
        fakeMoreProps,
        true, // pathCompleted
        true, // skipAddLastPoint
      );
    }
  }

  private readonly handleStart = (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const xy = xyValue as [number, number];
    const { sharedState } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const { currentPoints, type } = sharedState ?? ({} as ElliottImpulseSharedState);
    if (!isDefined(xy)) return;

    const now = Date.now();

    // Double-click
    if (this.isDoubleClick(now) && sharedState?.type === ShapeType.PATH) {
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
      }
      this.handleDoubleClickForPath(e, xy, moreProps);
      return;
    }

    // PATH completion by clicking last point
    if (sharedState && this.isCompletingPathWithLastPoint(xy, sharedState)) {
      this.handleEnd(e, xy, moreProps, true, true);
      return;
    }

    // Complete PATH if user clicks on last point again
    if (type === ShapeType.PATH && currentPoints && currentPoints.length > 1) {
      const lastPoint = currentPoints[currentPoints.length - 1];
      if (this.isClickOnLastPoint(xy, lastPoint)) {
        if (this.clickTimeout) {
          clearTimeout(this.clickTimeout);
          this.clickTimeout = null;
        }
        this.handleEnd(e, xy, moreProps, true, true);
        return;
      }
    }

    // Normal single-click → delayed
    this.lastClickTime = now;
    this.clickTimeout = setTimeout(() => {
      this.clickTimeout = null;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
      this.handleDelayedSingleClick(e, xy, moreProps, sharedState ?? ({} as ElliottImpulseSharedState));
    }, 50);
  };

  private readonly handleEnd = (
    e: React.MouseEvent,
    xyValue: [number, number],
    moreProps: MoreProps,
    pathCompleted?: boolean,
    skipAddLastPoint?: boolean,
  ) => {
    const { sharedState } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const { currentPoints, endXY } = sharedState ?? ({} as ElliottImpulseSharedState);
    if (!this.props.enabled || !isDefined(currentPoints)) return;

    const updatedPoints = skipAddLastPoint ? [...currentPoints] : [...currentPoints, xyValue];

    const shouldComplete = pathCompleted || (updatedPoints.length === PATH_POINT_LIMIT && isDefined(endXY));

    if (shouldComplete) {
      const newImpulse = completeOrExtendPath(updatedPoints, this.props);
      this.setState({ elliottImpulses: newImpulse }, () => {
        this.props.onComplete?.(e, newImpulse, moreProps);
      });
    } else {
      this.updateSharedStatePoints(updatedPoints);
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent) => {
    const { override } = this.state;
    const { elliottImpulses, onDragComplete, hoverId } = this.props;

    if (isDefined(override) && override.uuid === hoverId) {
      const updatedImpulses = elliottImpulses.map(impulse => {
        if (impulse.uuid !== override.uuid) {
          return { ...impulse, selected: false };
        }
        let updatedPoints;
        if ('newPoints' in override) {
          updatedPoints = override.newPoints;
        } else {
          updatedPoints = impulse.points.map((pt: [number, number], idx: number) =>
            idx === override.pointIdx ? override.newPoint : pt,
          );
        }

        return {
          ...impulse,
          points: updatedPoints,
          updatedAt: Date.now(),
          selected: true,
        };
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
      onDragComplete?.(e, updatedImpulses, {} as MoreProps);
      this.setState({ override: undefined });
    }
  };

  private readonly handleDrag = (
    e: React.MouseEvent,
    uuid: string,
    pointIdx: number,
    newPoint: [number, number] | [number, number][],
  ) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (isDefined(override)) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    if (pointIdx === -1 && Array.isArray(newPoint)) {
      this.setState({
        override: {
          uuid,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          newPoints: newPoint as [number, number][],
        },
      });
    } else {
      this.setState({
        override: {
          uuid,
          pointIdx,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          newPoint: newPoint as [number, number],
        },
      });
    }
  };

  private isClickOnLastPoint = (xy: [number, number], last: [number, number], tolerance = 1) => {
    const dx = xy[0] - last[0];
    const dy = xy[1] - last[1];
    return Math.sqrt(dx * dx + dy * dy) <= tolerance;
  };

  private isDoubleClick(now: number) {
    const DOUBLE_CLICK_DELAY = 250;
    return now - this.lastClickTime < DOUBLE_CLICK_DELAY;
  }

  private handleDoubleClickForPath(e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.suppressNextDoubleClick = true;
    this.lastClickTime = 0;
    e.stopPropagation();

    this.handleEnd(e, xyValue, moreProps, true, true);
  }

  private isCompletingPathWithLastPoint(xyValue: [number, number], sharedState: ElliottImpulseSharedState) {
    if (sharedState.type !== ShapeType.PATH) return false;
    const { currentPoints } = sharedState;

    if (!currentPoints || currentPoints.length <= 1) return false;

    const lastPoint = currentPoints[currentPoints.length - 1];
    return this.isClickOnLastPoint(xyValue, lastPoint);
  }

  private updateSharedStatePoints(updatedPoints: [number, number][]) {
    const { sharedState, setSharedState } = this.props;
    setSharedState?.({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      ...(sharedState as ElliottImpulseSharedState),
      currentPoints: updatedPoints,
    });
  }

  private handleDelayedSingleClick = (
    e: React.MouseEvent,
    xyValue: [number, number],
    moreProps: MoreProps,
    sharedState: ElliottImpulseSharedState,
  ) => {
    const { onStart } = this.props;
    const { status, currentPoints } = sharedState;

    if (!currentPoints?.length && status !== 'started') {
      onStart?.(e, [xyValue], moreProps);
      return;
    }

    const yValue = applyYClampIfNeeded(xyValue, moreProps, sharedState);
    this.handleEnd(e, [xyValue[0], yValue], moreProps);
  };
}
